import { AgentSocket, type AgentFrame } from './agent-socket';
import { sendMessage } from './agent-api';
import { emptyState, reduce, type ChatItem, type ChatState } from './agent-reducer';
import { keyboardOffset } from './viewport';
import { notifyTurnDone, turnJustFinished } from './notify';
import { soundEnabled } from './settings';
import type { ConnStatus } from './ws-client';

const STATUS_LABEL: Record<ConnStatus, string> = {
  connecting: 'Connecting…',
  connected: 'Connected',
  reconnecting: 'Reconnecting…',
  offline: 'Offline',
};

// summarizeToolInput renders a tool's raw input object as a single short line for
// the tool card. It prefers the field that best identifies the action (command,
// path, pattern, …); otherwise it falls back to compact JSON. Pure + exported so
// the formatting is unit-testable.
export function summarizeToolInput(input: unknown): string {
  if (input == null) return '';
  if (typeof input === 'string') return oneLine(input);
  if (typeof input !== 'object') return oneLine(String(input));
  const obj = input as Record<string, unknown>;
  const keys = ['command', 'file_path', 'path', 'pattern', 'url', 'query', 'prompt', 'description'];
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'string' && v) return oneLine(v);
  }
  return oneLine(JSON.stringify(obj));
}

// oneLine collapses whitespace to single spaces and truncates to keep tool cards
// to a single tidy row.
function oneLine(s: string, max = 96): string {
  const flat = s.replace(/\s+/g, ' ').trim();
  return flat.length > max ? `${flat.slice(0, max - 1)}…` : flat;
}

// renderItem builds a transcript row as a DOM node. User/assistant/error text is
// set via textContent (never innerHTML) so agent output can't inject markup.
function renderItem(item: ChatItem): HTMLElement {
  if (item.kind === 'tool') {
    const card = document.createElement('div');
    card.className = 'agent-tool';
    const name = document.createElement('span');
    name.className = 'agent-tool-name';
    name.textContent = item.name;
    const arg = document.createElement('span');
    arg.className = 'agent-tool-arg';
    arg.textContent = summarizeToolInput(item.input);
    card.append(name, arg);
    return card;
  }
  const row = document.createElement('div');
  row.className = `agent-msg agent-${item.kind}`;
  const bubble = document.createElement('div');
  bubble.className = 'agent-bubble';
  bubble.textContent = item.text;
  row.append(bubble);
  return row;
}

// openAgentView mounts the chat screen into `parent` (a flex column whose header
// already lives above it). It renders the transcript, a composer pinned to the
// bottom that floats above the on-screen keyboard, and wires the live event
// socket. Returns a dispose() that closes the socket and removes all listeners.
export function openAgentView(parent: HTMLElement, agentId: string): () => void {
  const log = document.createElement('div');
  log.className = 'agent-log';

  const composer = document.createElement('form');
  composer.className = 'agent-composer';
  const textarea = document.createElement('textarea');
  textarea.className = 'agent-input';
  textarea.rows = 1;
  textarea.placeholder = 'Message Claude…';
  textarea.setAttribute('aria-label', 'Message');
  const sendBtn = document.createElement('button');
  sendBtn.type = 'submit';
  sendBtn.className = 'agent-send primary';
  sendBtn.textContent = 'Send';
  composer.append(textarea, sendBtn);
  parent.append(log, composer);

  // WS is the single source of truth: it replays history on connect then streams
  // live frames, so we reset the transcript on every (re)connect and re-fold.
  let state: ChatState = emptyState();
  // Texts we optimistically rendered and are awaiting the server's user echo for,
  // so the echo doesn't double-render. Cleared on reconnect (replay rebuilds).
  let pendingUser: string[] = [];

  const statusEl = document.getElementById('conn-status');
  const setStatus = (s: ConnStatus) => {
    if (!statusEl) return;
    statusEl.textContent = STATUS_LABEL[s];
    statusEl.dataset.status = s;
  };

  // render rebuilds the log from `state`. Cheap enough for M5 (no streaming
  // deltas — one node per message), and keeps rendering a pure function of state.
  const render = () => {
    log.replaceChildren(...state.items.map(renderItem));
    if (state.busy) log.append(typingIndicator());
    // Auto-scroll to the newest content.
    log.scrollTop = log.scrollHeight;
  };

  const sock = new AgentSocket(agentId, {
    onStatus: (s) => {
      // A fresh connection replays history from scratch — discard local state so
      // we don't stack the replay on top of what we already showed.
      if (s === 'connecting' || s === 'reconnecting') {
        state = emptyState();
        pendingUser = [];
      }
      setStatus(s);
    },
    onEvent: (frame: AgentFrame) => {
      // Drop the server echo of a prompt we already optimistically rendered.
      if (frame.type === 'user' && pendingUser[0] === frame.text) {
        pendingUser.shift();
        return;
      }
      const wasBusy = state.busy;
      state = reduce(state, frame);
      // A completed turn (busy→idle) means Claude is waiting on the user — alert
      // if sound is on and the tab isn't in focus (notify gates this itself).
      if (turnJustFinished(wasBusy, state.busy)) notifyTurnDone(soundEnabled());
      render();
    },
  });

  const submit = () => {
    const text = textarea.value.trim();
    if (!text) return;
    textarea.value = '';
    autosize();
    // Optimistic: show the user bubble and busy indicator immediately.
    pendingUser.push(text);
    state = reduce(state, { type: 'user', text });
    state = reduce(state, { type: 'state', busy: true });
    render();
    void sendMessage(agentId, text).catch(() => {
      state = reduce(state, { type: 'error', message: 'Failed to send. Check your connection.' });
      render();
    });
    textarea.focus();
  };

  composer.addEventListener('submit', (e) => {
    e.preventDefault();
    submit();
  });
  // Enter sends; Shift+Enter inserts a newline (desktop affordance).
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };
  textarea.addEventListener('keydown', onKey);

  // autosize grows the textarea with its content up to a cap, so multi-line
  // prompts are visible without a separate scroll region.
  const autosize = () => {
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 140)}px`;
  };
  textarea.addEventListener('input', autosize);

  // layout floats the composer just above the on-screen keyboard (0 when closed)
  // using the same visualViewport math as the terminal toolbar, and pads the log
  // so the last message clears the composer.
  const vv = window.visualViewport;
  const layout = () => {
    const off = vv
      ? keyboardOffset({ innerHeight: window.innerHeight, vvHeight: vv.height, vvOffsetTop: vv.offsetTop })
      : 0;
    composer.style.bottom = `${off}px`;
    composer.classList.toggle('kb-open', off > 0);
    log.style.paddingBottom = `${composer.offsetHeight + 12}px`;
    log.scrollTop = log.scrollHeight;
  };
  window.addEventListener('resize', layout);
  vv?.addEventListener('resize', layout);
  vv?.addEventListener('scroll', layout);
  setTimeout(layout, 50);

  render();

  return () => {
    window.removeEventListener('resize', layout);
    vv?.removeEventListener('resize', layout);
    vv?.removeEventListener('scroll', layout);
    textarea.removeEventListener('keydown', onKey);
    sock.close();
    log.remove();
    composer.remove();
  };
}

// typingIndicator is the three-dot "Claude is working…" bubble shown while busy.
function typingIndicator(): HTMLElement {
  const row = document.createElement('div');
  row.className = 'agent-msg agent-assistant';
  const bubble = document.createElement('div');
  bubble.className = 'agent-bubble agent-typing';
  bubble.setAttribute('aria-label', 'Claude is working');
  bubble.innerHTML = '<span></span><span></span><span></span>';
  row.append(bubble);
  return row;
}
